import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ContractModal from '../components/ContractModal';
import { useLists, RowCheckbox, SelectionBar, ListFilter, ListDots } from '../components/Lists';

const STATUS_OPTIONS = ['Lead', 'Contacted', 'Negotiating', 'Under Contract', 'Dead'];

const STATUS_COLOR = {
  'Lead':           '#6B6760',
  'Contacted':      '#E0A526',
  'Negotiating':    '#6B6760',
  'Under Contract': '#14504A',
  'Dead':           '#B85450',
};

const SOURCE_OPTIONS = [
  'Driving for Dollars', 'Direct Mail', 'Cold Call', 'Referral',
  'Website Lead', 'Auction', 'Probate List', 'Skip Trace', 'Other',
];

function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

export default function OffMarket() {
  const navigate = useNavigate();
  const [listings, setListings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [listFilter, setListFilter]   = useState(null);
  const { memberSet } = useLists();

  const load = () => {
    setLoading(true);
    fetch('/api/off-market')
      .then(r => r.json())
      .then(data => { setListings(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    let result = listings;
    if (statusFilter !== 'All') result = result.filter(l => l.status === statusFilter);
    if (listFilter) result = result.filter(l => memberSet(listFilter).has(`off_market:${l.id}`));
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        (l.address || '').toLowerCase().includes(q) ||
        (l.zip || '').includes(q) ||
        (l.contact_name || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [listings, search, statusFilter, listFilter, memberSet]);

  const toggleSelect = (id, on) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const selectionItems = [...selectedIds].map(id => ({ item_type: 'off_market', item_id: String(id) }));

  const counts = useMemo(() => {
    const c = { All: listings.length };
    for (const s of STATUS_OPTIONS) c[s] = listings.filter(l => l.status === s).length;
    return c;
  }, [listings]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', margin: '-32px -36px' }}>
      {/* Top bar */}
      <div style={{ padding: '20px 28px 0', background: 'var(--navy)', borderBottom: '1px solid var(--navy-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 2 }}>Off Market Deals</h1>
            <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>{listings.length} self-sourced lead{listings.length === 1 ? '' : 's'}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search address, zip, contact…"
              style={{
                padding: '7px 12px', borderRadius: 8,
                border: '1px solid var(--navy-border)', background: 'var(--navy-card)',
                color: 'var(--white)', fontSize: 12, width: 220, outline: 'none',
              }}
            />
            <ListFilter value={listFilter} onChange={setListFilter} />
            <button onClick={load} title="Refresh" style={btnGhost()}>
              <RefreshIcon />
            </button>
            <button onClick={() => setImportModalOpen(true)} style={btnOutline('#14504A')}>
              <ImportIcon /> Import List
            </button>
            <button onClick={() => setAddModalOpen(true)} style={btnSolid('var(--teal)')}>
              <PlusIcon /> Add Lead
            </button>
          </div>
        </div>
        {/* Status pills */}
        <div style={{ display: 'flex', gap: 4 }}>
          {['All', ...STATUS_OPTIONS].map(s => {
            const active = statusFilter === s;
            const color = s === 'All' ? '#6B6760' : STATUS_COLOR[s];
            return (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding: '6px 14px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: active ? 700 : 400,
                background: active ? 'var(--navy-card)' : 'transparent',
                color: active ? color : 'var(--gray-500)',
                borderTop: active ? `2px solid ${color}` : '2px solid transparent',
                marginBottom: -1,
              }}>
                {s} {counts[s] > 0 && <span style={{ fontSize: 10, opacity: 0.7 }}>({counts[s]})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div style={{ padding: '12px 28px 0', flexShrink: 0 }}>
          <SelectionBar count={selectedIds.size} items={selectionItems} onClear={() => setSelectedIds(new Set())} />
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* List */}
        <div style={{ width: 380, overflowY: 'auto', borderRight: '1px solid var(--navy-border)', background: 'var(--navy)' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
              {listings.length === 0
                ? 'No off-market leads yet. Click "Import List" to paste addresses or "Add Lead" to enter manually.'
                : 'No leads match this filter.'}
            </div>
          ) : filtered.map(l => (
            <LeadCard key={l.id} lead={l} selected={selected?.id === l.id} onClick={() => setSelected(l)}
              checked={selectedIds.has(l.id)} onToggleSelect={toggleSelect} />
          ))}
        </div>

        {/* Detail */}
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--navy)' }}>
          {selected
            ? <DetailPanel lead={selected} onUpdate={(patch) => {
                fetch(`/api/off-market/${selected.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
                  .then(() => { load(); setSelected(s => ({ ...s, ...patch })); });
              }}
              onAnalyzed={(vals) => {
                setSelected(s => ({ ...s, ...vals }));
                setListings(ls => ls.map(item => item.id === selected.id ? { ...item, ...vals } : item));
              }}
              onDelete={() => {
                if (!window.confirm('Delete this lead?')) return;
                fetch(`/api/off-market/${selected.id}`, { method: 'DELETE' }).then(() => { setSelected(null); load(); });
              }}
              onContract={() => setContractOpen(true)}
              navigate={navigate}
            />
            : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--gray-500)', fontSize: 14, gap: 12 }}>
                <HouseIcon />
                Select a lead to view details
              </div>
          }
        </div>
      </div>

      {/* Modals */}
      {addModalOpen && <AddLeadModal onClose={() => setAddModalOpen(false)} onSaved={(row) => { setAddModalOpen(false); load(); setSelected(row); }} />}
      {importModalOpen && <ImportModal onClose={() => setImportModalOpen(false)} onImported={() => { setImportModalOpen(false); load(); }} />}
      {contractOpen && selected && (
        <ContractModal
          deal={{
            address: selected.address,
            city: selected.city, state: selected.state, zip: selected.zip,
            beds: selected.beds, baths: selected.baths, sqft: selected.sqft, year_built: selected.year_built,
            list_price: selected.asking_price, ai_arv: selected.ai_arv, max_offer: selected.max_offer,
            repair_cost: selected.repair_cost,
            description: selected.notes,
            photo_url: selected.photo_url,
            lat: selected.lat, lng: selected.lng,
            contact_name: selected.contact_name, contact_email: selected.contact_email, contact_phone: selected.contact_phone,
            _off_market_id: selected.id,
          }}
          dealId={selected.deal_id}
          offMarketMode
          onClose={() => setContractOpen(false)}
          // Leave the modal open on its result screen (download / send). Record the deal the backend
          // just created so a second generate on this lead reuses it instead of minting another deal.
          onGenerated={(data) => {
            if (data?.deal_id) setSelected(s => (s ? { ...s, deal_id: data.deal_id } : s));
            load();
          }}
        />
      )}
    </div>
  );
}

// ── LeadCard ──────────────────────────────────────────────────────────────────

function LeadCard({ lead: l, selected, onClick, checked, onToggleSelect }) {
  const sc = STATUS_COLOR[l.status] || '#6B6760';
  return (
    <div
      onClick={onClick}
      style={{
        padding: '12px 14px', borderBottom: '1px solid var(--navy-border)',
        cursor: 'pointer',
        background: selected ? 'var(--navy-card)' : 'transparent',
        borderLeft: selected ? '3px solid var(--teal)' : '3px solid transparent',
        transition: 'all 0.12s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 8 }}>
        <div style={{ paddingTop: 1 }} onClick={e => e.stopPropagation()}>
          <RowCheckbox checked={!!checked} onChange={(on) => onToggleSelect(l.id, on)} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.address}</span>
            <ListDots type="off_market" id={l.id} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            {[l.city, l.state, l.zip].filter(Boolean).join(', ')}
          </div>
        </div>
        <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 8, background: `${sc}15`, color: sc, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', marginLeft: 6 }}>
          {l.status}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          {[l.beds && `${l.beds}bd`, l.baths && `${l.baths}ba`, l.sqft && `${l.sqft.toLocaleString()} sqft`].filter(Boolean).join(' · ') || '—'}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)' }}>{fmtMoney(l.asking_price)}</div>
      </div>
      {l.source_type && (
        <div style={{ marginTop: 4, fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {l.source_type}
        </div>
      )}
    </div>
  );
}

// ── DetailPanel ──────────────────────────────────────────────────────────────

function DetailPanel({ lead: l, onUpdate, onAnalyzed, onDelete, onContract, navigate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(l);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showComps, setShowComps] = useState(false);
  React.useEffect(() => {
    setForm(l);
    setAnalysisResult(null);
    setAnalysisError(null);
    setShowComps(false);
  }, [l.id]);

  function save() {
    onUpdate(form);
    setEditing(false);
  }

  async function runAnalysis() {
    setAnalysisError(null);
    if (!l.zip || !l.sqft) {
      setAnalysisError('Zip and sqft are required. Edit the lead to add them, then re-run.');
      return;
    }
    setAnalyzing(true);
    try {
      const r = await fetch(`/api/off-market/${l.id}/analyze`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Analysis failed');
      setAnalysisResult(data);
      setShowComps(data.comps?.length > 0);
      onAnalyzed && onAnalyzed({
        ai_arv: data.ai_arv,
        repair_cost: data.repair_cost,
        max_offer: data.max_offer,
        analyzed_at: data.analyzed_at,
      });
    } catch (err) {
      setAnalysisError(err.message);
    }
    setAnalyzing(false);
  }

  const sc = STATUS_COLOR[l.status] || '#6B6760';
  const hasAnalysis = !!(l.ai_arv || analysisResult);
  const canAnalyze = !!(l.zip && l.sqft);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: `${sc}15`, color: sc, marginRight: 8 }}>{l.status}</span>
          {l.deal_id && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: 'rgba(20, 80, 74,0.15)', color: 'var(--teal)' }}>IN PIPELINE</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {l.deal_id && (
            <button onClick={() => navigate(`/underwriting/${l.deal_id}`)} style={btnOutline('var(--teal)')}>
              View Deal →
            </button>
          )}
          {!editing
            ? <button onClick={() => setEditing(true)} style={btnGhost()}>Edit</button>
            : <>
                <button onClick={() => { setEditing(false); setForm(l); }} style={btnGhost()}>Cancel</button>
                <button onClick={save} style={btnSolid('var(--teal)')}>Save</button>
              </>
          }
          <button onClick={onDelete} style={btnGhost('#B85450')}>Delete</button>
        </div>
      </div>

      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', margin: '8px 0 4px' }}>{l.address}</h2>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 20 }}>
        {[l.city, l.state, l.zip].filter(Boolean).join(', ')}
        {l.source_type && <span> · Source: <span style={{ color: 'var(--gray-300)' }}>{l.source_type}</span></span>}
      </div>

      {/* Property details grid */}
      <Section title="Property Details">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Beds" value={form.beds} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, beds: parseInt(v) || null }))} />
          <Field label="Baths" value={form.baths} editing={editing} type="number" step="0.5" onChange={v => setForm(f => ({ ...f, baths: parseFloat(v) || null }))} />
          <Field label="Sqft" value={form.sqft} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, sqft: parseInt(v) || null }))} />
          <Field label="Year Built" value={form.year_built} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, year_built: parseInt(v) || null }))} />
          <Field label="Asking Price" value={form.asking_price} display={fmtMoney(l.asking_price)} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, asking_price: parseInt(v) || null }))} />
          <Field label="Status" value={form.status} editing={editing} type="select" options={STATUS_OPTIONS} onChange={v => setForm(f => ({ ...f, status: v }))} />
        </div>
      </Section>

      {/* Underwriting numbers */}
      <Section
        title="Underwriting"
        actions={!editing && (
          <button
            onClick={runAnalysis}
            disabled={analyzing || !canAnalyze}
            title={!canAnalyze ? 'Zip and sqft required to run comp analysis' : 'Run comp-based ARV / Rehab / MAO'}
            style={{ ...btnOutline('var(--teal)'), opacity: (!canAnalyze || analyzing) ? 0.5 : 1, cursor: (!canAnalyze || analyzing) ? 'not-allowed' : 'pointer' }}
          >
            <SparkIcon /> {analyzing ? 'Analyzing…' : hasAnalysis ? 'Re-run Analysis' : 'Run Analysis'}
          </button>
        )}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Est. ARV" value={form.ai_arv} display={fmtMoney(l.ai_arv)} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, ai_arv: parseInt(v) || null }))} />
          <Field label="Repair Cost" value={form.repair_cost} display={fmtMoney(l.repair_cost)} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, repair_cost: parseInt(v) || null }))} />
          <Field label="MAO" value={form.max_offer} display={fmtMoney(l.max_offer)} editing={editing} type="number" onChange={v => setForm(f => ({ ...f, max_offer: parseInt(v) || null }))} />
        </div>
        {analysisError && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', border: '1px solid rgba(184, 84, 80,0.25)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>
            {analysisError}
          </div>
        )}
        {analysisResult && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--navy-border)', paddingTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', flex: 1 }}>{analysisResult.arv_source_label}</div>
              {analysisResult.comps?.length > 0 && (
                <button onClick={() => setShowComps(s => !s)} style={btnLink()}>
                  {showComps ? 'Hide' : 'View'} comps ({analysisResult.comps.length})
                </button>
              )}
            </div>
            {showComps && analysisResult.comps?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {analysisResult.comps.map(c => (
                  <CompRow key={c.id} comp={c} />
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Seller contact */}
      <Section title="Seller Contact">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Name" value={form.contact_name} editing={editing} onChange={v => setForm(f => ({ ...f, contact_name: v }))} />
          <Field label="Phone" value={form.contact_phone} editing={editing} onChange={v => setForm(f => ({ ...f, contact_phone: v }))} />
          <Field label="Email" value={form.contact_email} editing={editing} onChange={v => setForm(f => ({ ...f, contact_email: v }))} fullWidth />
        </div>
      </Section>

      {/* Notes */}
      <Section title="Notes">
        <Field label="" value={form.notes} editing={editing} multiline onChange={v => setForm(f => ({ ...f, notes: v }))} fullWidth />
        {!editing && !l.notes && <div style={{ fontSize: 12, color: 'var(--gray-500)', fontStyle: 'italic' }}>No notes yet</div>}
      </Section>

      {/* Actions */}
      {!editing && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <button onClick={onContract} style={btnSolid('#E0A526')}>
            <DocIcon /> Generate Contract
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, actions, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</div>
        {actions}
      </div>
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 14 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, display, editing, onChange, type = 'text', step, multiline, options, fullWidth }) {
  if (!editing) {
    return (
      <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
        {label && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>}
        <div style={{ fontSize: 13, color: value || display ? 'var(--gray-300)' : 'var(--gray-500)', whiteSpace: multiline ? 'pre-wrap' : 'nowrap', overflow: multiline ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}>
          {display !== undefined ? display : (value != null && value !== '' ? value : '—')}
        </div>
      </div>
    );
  }
  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 6,
    border: '1px solid var(--navy-border)', background: 'var(--navy)',
    color: 'var(--white)', fontSize: 12, outline: 'none', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
      {label && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>}
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : multiline ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
      ) : (
        <input type={type} value={value || ''} step={step} onChange={e => onChange(e.target.value)} style={inputStyle} />
      )}
    </div>
  );
}

// ── Add Lead Modal ───────────────────────────────────────────────────────────

function AddLeadModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    address: '', city: 'Austin', state: 'TX', zip: '',
    source_type: 'Driving for Dollars',
    contact_name: '', contact_phone: '', contact_email: '',
    notes: '',
    asking_price: '',
    beds: '', baths: '', sqft: '', year_built: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!form.address.trim()) { setError('Address is required'); return; }
    setSaving(true);
    try {
      const r = await fetch('/api/off-market', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          asking_price: parseInt(form.asking_price) || null,
          beds: parseInt(form.beds) || null,
          baths: parseFloat(form.baths) || null,
          sqft: parseInt(form.sqft) || null,
          year_built: parseInt(form.year_built) || null,
        }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      onSaved(data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title="Add Off-Market Lead" onClose={onClose} maxWidth={560}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <ModalField label="Address" required value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} fullWidth placeholder="1234 Main St" />
        <ModalField label="City" value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
        <ModalField label="Zip" value={form.zip} onChange={v => setForm(f => ({ ...f, zip: v }))} />
        <ModalField label="Beds" type="number" value={form.beds} onChange={v => setForm(f => ({ ...f, beds: v }))} />
        <ModalField label="Baths" type="number" step="0.5" value={form.baths} onChange={v => setForm(f => ({ ...f, baths: v }))} />
        <ModalField label="Sqft" type="number" value={form.sqft} onChange={v => setForm(f => ({ ...f, sqft: v }))} />
        <ModalField label="Year Built" type="number" value={form.year_built} onChange={v => setForm(f => ({ ...f, year_built: v }))} />
        <ModalField label="Asking Price" type="number" value={form.asking_price} onChange={v => setForm(f => ({ ...f, asking_price: v }))} />
        <ModalField label="Source" type="select" options={SOURCE_OPTIONS} value={form.source_type} onChange={v => setForm(f => ({ ...f, source_type: v }))} />
        <ModalField label="Contact Name" value={form.contact_name} onChange={v => setForm(f => ({ ...f, contact_name: v }))} />
        <ModalField label="Phone" value={form.contact_phone} onChange={v => setForm(f => ({ ...f, contact_phone: v }))} />
        <ModalField label="Email" value={form.contact_email} onChange={v => setForm(f => ({ ...f, contact_email: v }))} />
        <ModalField label="Notes" value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} multiline fullWidth />
      </div>
      {error && <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={btnGhost()}>Cancel</button>
        <button onClick={save} disabled={saving} style={btnSolid('var(--teal)')}>{saving ? 'Saving…' : 'Add Lead'}</button>
      </div>
    </Modal>
  );
}

// ── Import Modal ─────────────────────────────────────────────────────────────

function ImportModal({ onClose, onImported }) {
  const [text, setText] = useState('');
  const [sourceType, setSourceType] = useState('Imported List');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function doImport() {
    if (!text.trim()) { setError('Paste at least one address'); return; }
    setImporting(true);
    setError(null);
    try {
      const r = await fetch('/api/off-market/import', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, source_type: sourceType }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (err) {
      setError(err.message);
    }
    setImporting(false);
  }

  return (
    <Modal title="Import Off-Market Leads" onClose={onClose} maxWidth={620}>
      {result ? (
        <div>
          <div style={{ padding: '14px 16px', background: 'rgba(74, 124, 89,0.08)', border: '1px solid rgba(74, 124, 89,0.3)', borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4A7C59' }}>
              ✓ Imported {result.inserted} of {result.parsed_count} parsed addresses
            </div>
            {result.parsed_count > result.inserted && (
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
                {result.parsed_count - result.inserted} skipped (duplicate or invalid)
              </div>
            )}
          </div>
          <button onClick={onImported} style={{ ...btnSolid('var(--teal)'), width: '100%' }}>Done</button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-300)', marginBottom: 10 }}>
            Paste one address per line, or paste a CSV (with column headers like <code>address, city, zip, beds, baths, sqft</code>).
          </div>
          <ModalField label="Source" type="select" options={SOURCE_OPTIONS} value={sourceType} onChange={setSourceType} />
          <div style={{ height: 10 }} />
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={12}
            placeholder={`1234 Main St, Austin, TX 78704\n5678 Oak Ave, Austin, TX 78702\n9012 Cedar Dr, Austin, TX 78745\n\n— OR CSV format —\naddress,city,zip,beds,baths,sqft\n1234 Main St,Austin,78704,3,2,1400`}
            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, lineHeight: 1.5, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical' }}
          />
          {error && <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={btnGhost()}>Cancel</button>
            <button onClick={doImport} disabled={importing || !text.trim()} style={btnSolid('var(--teal)')}>
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Modal shell + form field ─────────────────────────────────────────────────

function Modal({ title, onClose, maxWidth = 480, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 28px 14px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 28px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, type = 'text', step, options, multiline, fullWidth, required, placeholder }) {
  const styleBase = {
    width: '100%', padding: '7px 10px', borderRadius: 6,
    border: '1px solid var(--navy-border)', background: 'var(--navy)',
    color: 'var(--white)', fontSize: 12, outline: 'none', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#B85450' }}> *</span>}
      </div>
      {type === 'select'
        ? <select value={value || ''} onChange={e => onChange(e.target.value)} style={styleBase}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        : multiline
          ? <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} placeholder={placeholder} style={{ ...styleBase, resize: 'vertical', lineHeight: 1.5 }} />
          : <input type={type} step={step} value={value || ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} style={styleBase} />
      }
    </div>
  );
}

// ── Buttons & Icons ──────────────────────────────────────────────────────────

function btnSolid(color) { return { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: color, border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }; }
function btnOutline(color) { return { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: `${color}12`, border: `1px solid ${color}40`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer' }; }
function btnGhost(color = 'var(--gray-500)') { return { padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }; }
function btnLink() { return { background: 'none', border: 'none', color: 'var(--teal)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }; }

function CompRow({ comp: c }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.address}</div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>
          {[
            c.sqft && `${c.sqft.toLocaleString()} sqft`,
            c.beds && `${c.beds}bd`,
            c.year_built && `built ${c.year_built}`,
            c.days_ago != null && `${c.days_ago}d ago`,
          ].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div style={{ textAlign: 'right', paddingLeft: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)' }}>{fmtMoney(c.sold_price)}</div>
        {c.price_per_sqft && <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>${c.price_per_sqft}/sqft</div>}
      </div>
    </div>
  );
}

function PlusIcon()    { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function ImportIcon()  { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>; }
function RefreshIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>; }
function DocIcon()     { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>; }
function HouseIcon()   { return <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>; }
function SparkIcon()   { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v6M12 16v6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M16 12h6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24"/></svg>; }
